import { IDIOMAS, useTraduccion, type Idioma } from '@tallerya/i18n';
import { IconoUsuario, Menu, type ElementoMenu } from '@tallerya/ui';
import { useAuth } from 'react-oidc-context';

// Los nombres van escritos en su propio idioma —«Español», no «Spanish»— porque
// quien busca cambiar de idioma normalmente no entiende el que está viendo.
const NOMBRES_IDIOMA: Record<Idioma, string> = {
  es: 'Español',
  en: 'English',
};

// Entrar, salir, y decir quién está adentro. Vive en el app bar (#99): con
// sesión es el nombre y el ícono del Figma, que abren un menú con el idioma y
// Salir; sin sesión, el botón para entrar.
//
// El idioma vive en este menú porque el Figma no le da otro lugar, y porque se
// cambia una vez y no se vuelve a tocar: no merece espacio fijo en la barra.
//
// Conviene tener presente que **ocultar no es proteger**: esconder un botón es
// comodidad para quien mira la pantalla. La comprobación que vale vive en el
// servidor.
export function Sesion() {
  const { t, idioma, cambiarIdioma } = useTraduccion();
  const auth = useAuth();

  if (auth.isLoading) {
    return <span className="sesion">{t('app.cargando')}</span>;
  }

  // Un fallo al identificarse no puede quedar en la consola del navegador: la
  // persona se queda mirando un botón que no hizo nada. El mensaje va corto y
  // el detalle al log, que es donde sirve.
  if (auth.error) {
    return (
      <span className="sesion">
        <span className="sesion__texto">{t('admin.sesion.error')}</span>
        <button type="button" className="sesion__boton" onClick={() => void auth.signinRedirect()}>
          {t('app.reintentar')}
        </button>
      </span>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <button type="button" className="sesion__boton" onClick={() => void auth.signinRedirect()}>
        {t('admin.sesion.entrar')}
      </button>
    );
  }

  // En la barra, el nombre, como en el Figma. `preferred_username` va siempre
  // como nota en el menú: desde #205 es el correo con el que se entró, y en una
  // computadora compartida importa saber de quién es la sesión. De qué taller es
  // lo dice el selector de taller, al lado.
  const perfil = auth.user?.profile;
  const usuario = perfil?.preferred_username ?? '';
  const quien = perfil?.name || usuario;

  const elementos: ElementoMenu[] = [
    ...(usuario ? [{ tipo: 'nota', texto: usuario } as const] : []),
    { tipo: 'grupo', etiqueta: t('app.idioma') },
    ...IDIOMAS.map((codigo) => ({
      etiqueta: NOMBRES_IDIOMA[codigo],
      marcada: codigo === idioma,
      onSeleccionar: () => cambiarIdioma(codigo),
    })),
    { tipo: 'divisor' },
    {
      etiqueta: t('admin.sesion.salir'),
      // `signoutRedirect` cierra la sesión **en Keycloak**, no solo acá. Con
      // `removeUser()` a secas, la pantalla diría que saliste y volver a entrar
      // no pediría credenciales: el servidor seguiría teniendo la sesión
      // abierta. En una computadora compartida eso es el bug que importa.
      onSeleccionar: () => void auth.signoutRedirect(),
    },
  ];

  return (
    <Menu
      elementos={elementos}
      disparador={({ alternar, atributos }) => (
        <button
          type="button"
          className="sesion__disparador"
          // En celular el nombre se esconde y queda solo el ícono; la etiqueta
          // es la que sigue diciendo de quién es la sesión.
          aria-label={t('admin.sesion.cuenta', { nombre: quien })}
          onClick={alternar}
          {...atributos}
        >
          <span className="sesion__nombre">{quien}</span>
          <IconoUsuario />
        </button>
      )}
    />
  );
}
